import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { Button } from '@ctw/shared/components/button';
import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { notify } from '@ctw/shared/components/toast';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ZusFhirResource, summarizeFhirResource } from '@zeus-health/types';
import { useCallback, useMemo } from 'react';
import { ClinicalEvidenceCard } from './clinical-evidence-card';
import {
  EVIDENCE_SECTION_ORDER,
  type EvidenceSectionKey,
  RESOURCE_TYPE_TO_SECTION,
  useEvidenceResources,
} from './use-evidence-resources';

function getSectionForResourceType(resourceType: string): EvidenceSectionKey | 'Other' {
  return RESOURCE_TYPE_TO_SECTION[resourceType] ?? 'Other';
}

export const RiskGapEvidence = ({ riskGap }: { riskGap: RiskGapModel }) => {
  const evidence = useEvidenceResources(riskGap);
  const {
    resources,
    resourceById,
    sortedReferences,
    referenceDisplayByResourceId,
    isLoading,
    hasEvidence,
  } = evidence;

  const telemetry = useTelemetry();
  const { openResourceDrawer } = useResourceDrawer();

  const summaries = useMemo(
    () =>
      resources.map((model) => {
        const summary = summarizeFhirResource(model.resource as ZusFhirResource);
        const display =
          model.resourceType === 'DocumentReference'
            ? referenceDisplayByResourceId.get(model.id)
            : undefined;
        const text = display ? `${display} — ${summary}` : summary;
        return { id: model.id, resourceType: model.resourceType, text };
      }),
    [resources, referenceDisplayByResourceId],
  );

  const groupedBySection = useMemo(() => {
    const order: Array<EvidenceSectionKey | 'Other'> = [...EVIDENCE_SECTION_ORDER, 'Other'];
    const groups = new Map<
      EvidenceSectionKey | 'Other',
      Array<{ id: string; resourceType: string; text: string }>
    >();
    for (const key of order) {
      groups.set(key, []);
    }
    for (const { id, resourceType, text } of summaries) {
      const section = getSectionForResourceType(resourceType);
      groups.get(section)?.push({ id, resourceType, text });
    }
    return order
      .map((section) => ({ section, items: groups.get(section) ?? [] }))
      .filter(({ items }) => items.length > 0);
  }, [summaries]);

  const copyContent = useMemo(
    () =>
      groupedBySection
        .map(
          ({ section, items }) => `${section}:\n${items.map(({ text }) => `- ${text}`).join('\n')}`,
        )
        .join('\n\n'),
    [groupedBySection],
  );

  const handleCopy = useCallback(() => {
    if (!copyContent) {
      return;
    }
    void copyToClipboard(copyContent, () => {
      notify({ type: 'info', title: 'Evidence summary copied to clipboard' });
    });
  }, [copyContent]);

  if (!hasEvidence) {
    return <div className={tw`text-content-subtle text-sm`}>No clinical evidence available.</div>;
  }

  if (isLoading) {
    return <div className={tw`text-content-subtle text-sm`}>Loading evidence…</div>;
  }

  return (
    <div className={tw`space-y-4`}>
      {summaries.length > 0 && (
        <div className={tw`overflow-hidden rounded-lg border border-border bg-white shadow-sm`}>
          <div
            className={tw`flex h-14 items-center justify-between border-border border-b bg-muted/30 px-4 py-3`}
          >
            <span
              className={tw`font-semibold text-muted-foreground text-xs uppercase tracking-wider`}
            >
              Evidence Summary
            </span>
            <Tooltip content="Copy summary" id="evidence-summary-copy">
              <Button
                ariaLabel="Copy summary to clipboard"
                className={tw`h-8 w-8 rounded-md`}
                disabled={!copyContent}
                onClick={handleCopy}
                size="sm"
                type="button"
                variant="unstyled"
              >
                <FontAwesomeIcon icon={faCopy} className={tw`h-4 w-4 text-muted-foreground`} />
              </Button>
            </Tooltip>
          </div>
          <div className={tw`px-3 py-4`}>
            <div className={tw`whitespace-pre-wrap text-sm`}>{copyContent}</div>
          </div>
        </div>
      )}

      <div className={tw`grid grid-cols-2 gap-4`}>
        {sortedReferences.map((reference) => {
          const model = resourceById.get(reference.resourceId);
          return (
            <ClinicalEvidenceCard
              key={`${reference.resourceType}-${reference.resourceId}`}
              reference={reference}
              model={model}
              onClick={model ? () => openResourceDrawer({ telemetry, model }) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};
